import type { AnalyticsIdentity } from './contract';
import {
  loadPendingDiagnosticUploads,
  markDiagnosticUploadComplete,
  markDiagnosticUploadFailed,
  type DiagnosticUploadItem,
} from '../storage/diagnostics-store';

export function diagnosticUploadHeaders(item: DiagnosticUploadItem, identity: AnalyticsIdentity): Headers {
  const headers = new Headers({
    'Content-Type': 'application/octet-stream',
    'Content-Encoding': item.encoding,
    'X-Match-Id': item.matchId,
    'X-Log-Sha256': item.sha256,
    'X-Log-Bytes': String(item.bytes),
    'X-Created-At': item.createdAt,
    'X-Engine-Version': item.engineVersion,
    'X-Content-Version': item.contentVersion,
    'X-Arena-Id': item.arenaId,
    'X-Has-Critical': item.critical ? '1' : '0',
    'X-Anonymous-Id': identity.anonymousId,
    'X-Deletion-Token': identity.deletionToken,
    'X-Match-Mode': item.matchMode,
    'X-Build-Id': item.buildId,
    'X-Termination-Reason': item.terminationReason,
  });
  if (item.networkMatchId) headers.set('X-Network-Match-Id', item.networkMatchId);
  if (item.networkSeat) headers.set('X-Network-Seat', item.networkSeat);
  return headers;
}

export class DiagnosticUploader {
  private active: Promise<void> | null = null;

  constructor(
    private readonly identity: AnalyticsIdentity,
    private readonly enabled: () => boolean,
    private readonly endpoint = '/api/diagnostics',
  ) {}

  process(): Promise<void> {
    if (!this.enabled()) return Promise.resolve();
    this.active ??= this.drain().finally(() => { this.active = null; });
    return this.active;
  }

  private async drain(): Promise<void> {
    const items = await loadPendingDiagnosticUploads();
    for (const item of items) {
      if (!this.enabled()) return;
      await this.upload(item);
    }
  }

  private async upload(item: DiagnosticUploadItem): Promise<void> {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: diagnosticUploadHeaders(item, this.identity),
        body: item.blob,
      });
      if (response.ok || response.status === 409 || response.status === 413 || response.status === 507) {
        await markDiagnosticUploadComplete(item.matchId);
        return;
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      await markDiagnosticUploadFailed(item, String(error));
    }
  }
}
